import React from 'react';
import moment from 'moment';
import {
    Box,
    Button,
    ClickAwayListener,
    Dialog,
    DialogContent,
    DialogTitle,
    Grow,
    IconButton,
    Paper,
    Popper,
    Tooltip,
    Typography,
    makeStyles,
    createStyles,
    Theme,
} from '@material-ui/core';
import NotificationsNoneOutlinedIcon from '@material-ui/icons/NotificationsNoneOutlined';
import NewReleasesOutlinedIcon from '@material-ui/icons/NewReleasesOutlined';
import CloseIcon from '@material-ui/icons/Close';
import QuestionAnswerOutlinedIcon from '@material-ui/icons/QuestionAnswerOutlined';
import { WandIcon } from '../icons/WandIcon';
import { AnimatedItem } from '../animations/AnimatedItem';
import { browserHistory, requestSupportWithNote } from '../AppContent';
import { linkToPage } from '../../App';
import { mainTheme } from '../../Theme';
import { AI_ACCENT, AI_ACCENT_SOFT } from '../../documentAi/aiTheme';

// Explicit request: hidden for now - single toggle to bring it back later.
const SHOW_RELEASE_SUPPORT_BUTTON = false;

// ─── Hardcoded notifications ────────────────────────────────────────────────
// Explicit request: no backend for this yet - a fixed sample list standing in for what a real
// notification feed would return, so the header/panel/dialog UI can be built and reviewed now.

type NotificationKind = 'release' | 'aiAssessment';

interface NotificationItem {
    id: string;
    kind: NotificationKind;
    title: string;
    timestamp: moment.Moment;
    preview: string;
    /** 'release' only - shown in the big dialog via dangerouslySetInnerHTML. Safe here (unlike
     * the AI analysis renderer elsewhere in this app) because this is static, developer-authored
     * copy, never user- or AI-generated content. Ignored when bodyUrl is set. */
    bodyHtml?: string;
    /** 'release' only - the real release page, embedded via iframe (same pattern Login.tsx's
     * own PromoIframe uses for external content) rather than fetched and injected as HTML,
     * which would mean trusting a third-party page's raw markup inside our own DOM. */
    bodyUrl?: string;
}

// Explicit request: the id identifies the release itself (its version number, e.g.
// release-2026.14), not an encoded date - timestamp is passed directly instead of being parsed
// back out of the id.
const buildReleaseNotification = (
    id: string,
    title: string,
    preview: string,
    timestamp: moment.Moment,
    body: { bodyUrl: string } | { bodyHtml: string },
): NotificationItem => ({
    id,
    kind: 'release',
    title,
    timestamp,
    preview,
    ...body,
});

// const RELEASE_NOTE_HTML_PREVIOUS = `
//     <h2>New features</h2>
//     <ul>
//         <li>A refreshed login screen.</li>
//     </ul>
//     <h2>Improvements</h2>
//     <ul>
//         <li>Smoother, more consistent animations throughout the app.</li>
//         <li>Clearer error messages when something goes wrong.</li>
//     </ul>
// `;

const NOTIFICATIONS: NotificationItem[] = [
    // Explicit request: release 2026.15 - upcoming, linked ahead of its 14-10-2026 release date
    // so the notification is ready the day it goes live.
    buildReleaseNotification(
        'release-2026.16',
        'Release 2026.16',
        'See what\'s new!',
        moment('02-11-2026', 'DD-MM-YYYY'),
        { bodyUrl: 'https://www.redraincorp.com/releaseupdates/202616' },
    ),
    buildReleaseNotification(
        'release-2026.15',
        'Release 2026.15',
        'See what\'s new!',
        moment('14-10-2026', 'DD-MM-YYYY'),
        { bodyUrl: 'https://www.redraincorp.com/releaseupdates/202615' },
    ),   
    // Explicit request: release 2026.14, released 28-09-2026.
    buildReleaseNotification(
        'release-2026.14',
        'Release 2026.14',
        'See what\'s new!',
        moment('28-09-2026', 'DD-MM-YYYY'),
        { bodyUrl: 'https://www.redraincorp.com/releaseupdates/202614' },
    ),
    buildReleaseNotification(
        'release-newSmartphoneApp',
        'New Smartphone App',
        'See what\'s new!',
        moment('26-09-2026', 'DD-MM-YYYY'),
        { bodyUrl: 'https://www.redraincorp.com/releaseupdates/newsmartphoneapp' },
    ),
    buildReleaseNotification(
        'release-2026.13',
        'Release 2026.13',
        'See what\'s new!',
        moment('14-09-2026', 'DD-MM-YYYY'),
        { bodyUrl: 'https://www.redraincorp.com/releaseupdates/202613' },
    ),
    // Explicit request: AI assessment notifications hidden for now - commented out (not
    // deleted) so they're a one-line restore once this is ready to turn back on.
    // {
    //     id: 'ai-1',
    //     kind: 'aiAssessment',
    //     title: 'AI assessment complete',
    //     timestamp: moment().subtract(3, 'hours'),
    //     preview: 'The AI analysis for "Aleena Gross" is ready to review.',
    // },
    // {
    //     id: 'ai-2',
    //     kind: 'aiAssessment',
    //     title: 'AI assessment complete',
    //     timestamp: moment().subtract(1, 'day'),
    //     preview: 'The AI analysis for "John West" is ready to review.',
    // },
    // buildReleaseNotification(
    //     // No known version number for this one (pre-existing sample data) - kept as its
    //     // original id rather than guessing a version to match 2026.14/2026.15's own naming.
    //     'release-15092026',
    //     'New features, improvements & UI updates',
    //     'A refreshed login screen and smoother animations throughout.',
    //     moment('15-09-2026', 'DD-MM-YYYY'),
    //     { bodyHtml: RELEASE_NOTE_HTML_PREVIOUS },
    // ),
    // {
    //     id: 'ai-3',
    //     kind: 'aiAssessment',
    //     title: 'AI assessment complete',
    //     timestamp: moment().subtract(2, 'days'),
    //     preview: 'The AI analysis for "Simon Katych" is ready to review.',
    // },
];

const NOTIFICATION_GROUP_LABELS: Record<NotificationKind, string> = {
    release: 'Product Updates',
    aiAssessment: 'AI Assessments',
};

// ─── Read tracking (localStorage) ───────────────────────────────────────────
// Explicit request: read state persists on the user's own PC, storing only notification ids -
// never titles/previews/HTML/anything else about the notification itself.
const READ_NOTIFICATION_IDS_STORAGE_KEY = 'rv-notification-read-ids';

// Explicit request: never show more than this many release notifications at a time.
const MAX_RELEASE_NOTIFICATIONS = 5;

const loadReadIds = (): Set<string> => {
    const raw = localStorage.getItem(READ_NOTIFICATION_IDS_STORAGE_KEY);
    if (!raw) {
        return new Set();
    }
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? new Set(parsed) : new Set();
    } catch {
        return new Set();
    }
};

const saveReadIds = (ids: Set<string>) => {
    localStorage.setItem(READ_NOTIFICATION_IDS_STORAGE_KEY, JSON.stringify(Array.from(ids)));
};

// ─── Layout ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        trigger: {
            position: 'relative',
            display: 'flex',
        },
        bellIcon: {
            color: mainTheme.TemplateColor.Primary,
        },
        // Same "breathing" animation language as DueListWidget.tsx's own iconChip (iconBreathe/
        // iconPing) - only while hasUnread is true, so "something needs attention" reads the
        // same way everywhere in the app it appears.
        bellUnread: {
            animation: '$bellBreathe 2200ms ease-in-out infinite alternate',
            '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
            },
        },
        // Explicit bug report: the pulse read as too subtle/small - the icon's own rest size is
        // unchanged (still scale(1) at the bottom of the alternate), only the peak grows.
        '@keyframes bellBreathe': {
            from: { transform: 'scale(1)' },
            to: { transform: 'scale(1.4)' },
        },
        redDot: {
            position: 'absolute',
            top: 8,
            right: 8,
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: '#E5484D',
            border: '2px solid #fff',
            pointerEvents: 'none',
            animation: '$dotPing 2000ms ease-out infinite',
            '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
            },
        },
        // Explicit bug report: same "too small" complaint as bellBreathe above - wider/stronger
        // ping ring (dot itself still 8px, only the expanding ring grows).
        '@keyframes dotPing': {
            '0%': { boxShadow: '0 0 0 0 rgba(229,72,77,0.65)' },
            '80%': { boxShadow: '0 0 0 12px rgba(229,72,77,0)' },
            '100%': { boxShadow: '0 0 0 12px rgba(229,72,77,0)' },
        },
        // Explicit request: a small toast under the bell, shown once right after login when
        // there are unread notifications, auto-dismissing after 10 seconds (see
        // showNewNotificationsToast) - trigger is already position:relative, so this just
        // anchors to it directly rather than needing its own Popper/anchorEl. Explicit bug
        // report: a solid Primary-blue fill read as too heavy - a solid, opaque pale blue (not
        // a transparent tint) reads as a much lighter touch instead.
        newNotificationsToast: {
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 10,
            zIndex: 10,
            backgroundColor: '#EAF1FF',
            color: 'var(--rv-accent-blue)',
            border: '1px solid #CCDFFF',
            padding: '8px 14px',
            borderRadius: 8,
            fontSize: 12.5,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
            '&::before': {
                content: '""',
                position: 'absolute',
                top: -5,
                right: 14,
                width: 10,
                height: 10,
                backgroundColor: 'inherit',
                transform: 'rotate(45deg)',
            },
        },
        panelPaper: {
            marginTop: theme.spacing(1),
            width: 380,
            maxWidth: '92vw',
            maxHeight: '70vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 12,
            border: `1px solid ${mainTheme.CommonColor.LightGrey2}`,
            boxShadow: '0 10px 40px rgba(35,37,59,0.16)',
            overflow: 'hidden',
        },
        panelBody: {
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
        },
        panelHeader: {
            padding: theme.spacing(1.5, 2),
            borderBottom: `1px solid ${mainTheme.CommonColor.LightGrey2}`,
            flexShrink: 0,
        },
        panelTitle: {
            fontWeight: 700,
            fontSize: 14,
        },
        panelList: {
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            padding: theme.spacing(1),
        },
        sectionHeading: {
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: mainTheme.TextColor.Tertiary,
            padding: theme.spacing(1.25, 1.25, 0.5),
        },
        card: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: theme.spacing(1.25),
            padding: theme.spacing(1.25),
            borderRadius: 10,
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
            '&:hover': {
                backgroundColor: mainTheme.CommonColor.LightBlue2,
            },
        },
        // Distinct from the hover colour above (a lighter tint) so hovering an unread card
        // still shows a visible change, not the same shade twice.
        // Explicit bug report: too blue - lighter than the old #F0F5FF, and a solid, opaque
        // colour rather than a transparent tint.
        cardUnread: {
            backgroundColor: '#F7FAFF',
        },
        cardTitleRow: {
            display: 'flex',
            alignItems: 'center',
            gap: 6,
        },
        unreadDot: {
            width: 6,
            height: 6,
            minWidth: 6,
            borderRadius: '50%',
            backgroundColor: mainTheme.TemplateColor.Primary,
        },
        cardIconChip: {
            width: 34,
            height: 34,
            minWidth: 34,
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        },
        cardBody: {
            minWidth: 0,
            flex: 1,
        },
        cardTitle: {
            fontSize: 13,
            fontWeight: 600,
            color: mainTheme.TextColor.Primary,
        },
        cardPreview: {
            fontSize: 12,
            color: mainTheme.TextColor.Secondary,
            marginTop: 2,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
        },
        cardTimestamp: {
            fontSize: 11,
            color: mainTheme.TextColor.Tertiary,
            marginTop: 4,
        },
        emptyState: {
            padding: theme.spacing(3),
            textAlign: 'center',
            fontSize: 13,
            color: mainTheme.TextColor.Secondary,
        },
        dialogTitle: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        dialogTitleActions: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
        },
        // Same style as AppHeader.tsx's own supportButton.
        dialogSupportButton: {
            color: mainTheme.TemplateColor.Primary,
            textTransform: 'none',
            fontSize: 13,
        },
        dialogHtml: {
            fontSize: 14,
            lineHeight: 1.6,
            '& h2': { fontSize: 16, fontWeight: 700, margin: '16px 0 8px' },
            '& h2:first-child': { marginTop: 0 },
            '& ul': { margin: '0 0 8px', paddingLeft: 20 },
            '& li': { marginBottom: 4 },
        },
        // bodyUrl case only - a fixed height so the iframe (height:100%) below has something
        // concrete to fill, same reasoning as Login.tsx's own PromoIframe/its flex-height
        // ancestor chain. padding:0 so the embedded page reads as its own full page, not one
        // boxed in with this dialog's usual content padding.
        // Explicit request: bigger/wider - taller content area (maxWidth bump on the Dialog
        // itself handles the width, see its own JSX usage).
        dialogContentFrame: {
            height: '88vh',
            padding: 0,
        },
        dialogIframe: {
            width: '100%',
            height: '100%',
            border: 'none',
            display: 'block',
        },
    }),
);

const NOTIFICATION_ICON_COLORS: Record<NotificationKind, { bg: string; fg: string }> = {
    release: { bg: '#FDECC8', fg: '#B9700A' },
    // Matches the Document AI feature's own accent (aiTheme.ts) - blue, not purple.
    aiAssessment: { bg: AI_ACCENT_SOFT, fg: AI_ACCENT },
};

export const NotificationCenter: React.FC = () => {
    const classes = useStyles();
    const [open, setOpen] = React.useState(false);
    const [readIds, setReadIds] = React.useState<Set<string>>(() => loadReadIds());
    const [openReleaseNote, setOpenReleaseNote] = React.useState<NotificationItem | null>(null);
    const [showNewNotificationsToast, setShowNewNotificationsToast] = React.useState(false);
    const anchorRef = React.useRef<HTMLButtonElement>(null);

    const notifications = React.useMemo(
        () => [...NOTIFICATIONS].sort((a, b) => b.timestamp.valueOf() - a.timestamp.valueOf()),
        [],
    );
    const hasUnread = notifications.some((notification) => !readIds.has(notification.id));

    // Explicit request: "New notifications available" toast under the bell, shown once right
    // after login (this component mounts fresh exactly then - see AppHeader.tsx's own
    // notificationCenterButton, which only renders it once isAuthenticated flips true) when
    // there are unread notifications, auto-dismissing after 10 seconds. Mount-only ([]) is
    // deliberate - this should only ever fire once per login, not every time hasUnread later
    // changes (e.g. after reading/marking something).
    React.useEffect(() => {
        if (!hasUnread) {
            return undefined;
        }
        setShowNewNotificationsToast(true);
        const dismissTimer = setTimeout(() => setShowNewNotificationsToast(false), 10000);
        return () => clearTimeout(dismissTimer);
    // tslint:disable-next-line: align
    }, []);

    // Explicit request: once a newer release notification exists, the previous one is
    // superseded - marked read automatically rather than left nagging as unread forever. Runs
    // once notifications is computed (a stable, module-level list with no real dependencies),
    // same as if the user had already seen and dismissed every release but the newest.
    React.useEffect(() => {
        const releaseNotifications = notifications.filter((notification) => notification.kind === 'release');
        const supersededIds = releaseNotifications.slice(1).map((notification) => notification.id);
        if (supersededIds.length === 0) {
            return;
        }
        setReadIds((prevReadIds) => {
            if (supersededIds.every((id) => prevReadIds.has(id))) {
                return prevReadIds;
            }
            const merged = new Set(prevReadIds);
            supersededIds.forEach((id) => merged.add(id));
            saveReadIds(merged);
            return merged;
        });
    // tslint:disable-next-line: align
    }, [notifications]);

    // Grouped under a heading per kind (explicit request) - notifications is already sorted
    // newest-first above, so whichever kind's own newest item comes first in that order is the
    // first group a Map preserves too, keeping the more recently-active group on top. Release
    // notifications are also capped to the 5 most recent (explicit request) - older ones are
    // already auto-superseded/read (see the effect above) by the time there'd ever be more than
    // one worth seeing, but the cap holds regardless of read state once the list grows that long.
    const groupedNotifications = React.useMemo(() => {
        const groups = new Map<NotificationKind, NotificationItem[]>();
        notifications.forEach((notification) => {
            const group = groups.get(notification.kind) ?? [];
            group.push(notification);
            groups.set(notification.kind, group);
        });
        const releaseGroup = groups.get('release');
        if (releaseGroup) {
            groups.set('release', releaseGroup.slice(0, MAX_RELEASE_NOTIFICATIONS));
        }
        return Array.from(groups.entries());
    }, [notifications]);

    const handleToggle = () => {
        // Explicit bug report: marking everything read the instant the panel opened meant the
        // unread/read styling on each item never actually had a chance to show while browsing -
        // by the time the list was visible, every item already read as read. Each item now
        // marks itself read individually when clicked (see markAsRead/onClickNotification
        // below), not the whole batch just from opening the panel.
        setOpen((current) => !current);
        // The toast would otherwise sit overlapping the now-open panel.
        setShowNewNotificationsToast(false);
    };

    const handleClose = (event: React.MouseEvent<EventTarget>) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }
        setOpen(false);
    };

    const markAsRead = (notification: NotificationItem) => {
        if (readIds.has(notification.id)) {
            return;
        }
        setReadIds((prevReadIds) => {
            const merged = new Set(prevReadIds);
            merged.add(notification.id);
            saveReadIds(merged);
            return merged;
        });
    };

    const onClickNotification = (notification: NotificationItem) => {
        markAsRead(notification);
        setOpen(false);
        if (notification.kind === 'release') {
            setOpenReleaseNote(notification);
            return;
        }
        // AI assessment notifications aren't tied to a real enquiry yet (hardcoded - explicit
        // request "clicking will open a new enquiry for now") - opens a fresh enquiry via
        // EnquirySummaryPage.tsx's own openCreateEnquiry location.state signal (see that
        // file's own comment, next to its identical quickViewEnquiryFilter handling).
        browserHistory.push({
            pathname: linkToPage('enquiriesSummary'),
            state: { openCreateEnquiry: true },
        });
    };

    return (
        <Box className={classes.trigger}>
            <Tooltip title="Notifications">
                <IconButton
                    aria-label="notifications"
                    size="small"
                    className="rv-header-action-hover"
                    ref={anchorRef}
                    onClick={handleToggle}
                >
                    <NotificationsNoneOutlinedIcon className={`${classes.bellIcon} ${hasUnread ? classes.bellUnread : ''}`} />
                    {hasUnread && <span className={classes.redDot} />}
                </IconButton>
            </Tooltip>
            {showNewNotificationsToast && (
                <AnimatedItem noFill={true}>
                    <div className={classes.newNotificationsToast}>
                        New notifications available
                    </div>
                </AnimatedItem>
            )}
            <Popper open={open} anchorEl={anchorRef.current} transition={true} disablePortal={true} placement="bottom-end">
                {({ TransitionProps }) => (
                    <Grow {...TransitionProps} style={{ transformOrigin: 'right top' }}>
                        <Paper className={classes.panelPaper}>
                            <ClickAwayListener onClickAway={handleClose}>
                                <Box className={classes.panelBody}>
                                    <Box className={classes.panelHeader}>
                                        <Typography className={classes.panelTitle}>Notifications</Typography>
                                    </Box>
                                    <Box className={classes.panelList}>
                                        {notifications.length === 0 ? (
                                            <Typography className={classes.emptyState}>You're all caught up.</Typography>
                                        ) : groupedNotifications.map(([kind, items], groupIndex) => (
                                            <Box key={kind}>
                                                <Typography className={classes.sectionHeading}>{NOTIFICATION_GROUP_LABELS[kind]}</Typography>
                                                {items.map((notification, itemIndex) => {
                                                    const isUnread = !readIds.has(notification.id);
                                                    return (
                                                        <AnimatedItem key={notification.id} noFill={true} delay={(groupIndex * 3 + itemIndex) * 30}>
                                                            <Box
                                                                className={`${classes.card} ${isUnread ? classes.cardUnread : ''}`}
                                                                onClick={() => onClickNotification(notification)}
                                                            >
                                                                <Box
                                                                    className={classes.cardIconChip}
                                                                    style={{
                                                                        backgroundColor: NOTIFICATION_ICON_COLORS[notification.kind].bg,
                                                                        color: NOTIFICATION_ICON_COLORS[notification.kind].fg,
                                                                    }}
                                                                >
                                                                    {notification.kind === 'release'
                                                                        ? <NewReleasesOutlinedIcon fontSize="small" />
                                                                        : <WandIcon fontSize="small" />}
                                                                </Box>
                                                                <Box className={classes.cardBody}>
                                                                    <Box className={classes.cardTitleRow}>
                                                                        {isUnread && <span className={classes.unreadDot} />}
                                                                        <Typography className={classes.cardTitle}>{notification.title}</Typography>
                                                                    </Box>
                                                                    <Typography className={classes.cardPreview}>{notification.preview}</Typography>
                                                                    <Typography className={classes.cardTimestamp}>{notification.timestamp.fromNow()}</Typography>
                                                                </Box>
                                                            </Box>
                                                        </AnimatedItem>
                                                    );
                                                })}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>

            <Dialog open={!!openReleaseNote} onClose={() => setOpenReleaseNote(null)} maxWidth="lg" fullWidth={true}>
                <DialogTitle disableTypography={true} className={classes.dialogTitle}>
                    <Typography variant="h6">{openReleaseNote?.title}</Typography>
                    <Box className={classes.dialogTitleActions}>
                        {/* Explicit request: same underlying support flow as AppHeader.tsx's own
                            supportButton (requestSupportWithNote - the same module-singleton
                            listener AiChatDialog.tsx already calls to open the support dialog
                            without prop-drilling through this nested popper/dialog tree), but
                            worded/framed for "ask a question about this release" rather than
                            the generic "report an issue" wording - see SupportDialogCustomization
                            on SupportEmailDialog, which only this caller overrides. */}
                        {SHOW_RELEASE_SUPPORT_BUTTON && (
                            <Tooltip title="Ask us about this release">
                                <Button
                                    size="small"
                                    className={classes.dialogSupportButton}
                                    aria-label="ask a question about this release"
                                    onClick={() => requestSupportWithNote(
                                        `Question about: ${openReleaseNote?.title ?? 'this release'}\n\n`,
                                        {
                                            title: 'Want to know more?',
                                            subtitle: 'Just ask here - we\'ll open your default email app with your question',
                                            description: 'Have a question about this release? Tell us what you\'d like to know and we\'ll get back to you.',
                                            subject: `Question about: ${openReleaseNote?.title ?? 'release'}`,
                                        },
                                    )}
                                    startIcon={<QuestionAnswerOutlinedIcon style={{ fontSize: 18 }} />}
                                >
                                    Want to know more? Just ask here
                                </Button>
                            </Tooltip>
                        )}
                        <IconButton size="small" onClick={() => setOpenReleaseNote(null)} aria-label="Close">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers={true} className={openReleaseNote?.bodyUrl ? classes.dialogContentFrame : undefined}>
                    {openReleaseNote?.bodyUrl ? (
                        <iframe
                            src={openReleaseNote.bodyUrl}
                            title={openReleaseNote.title}
                            className={classes.dialogIframe}
                        />
                    ) : (
                        <div className={classes.dialogHtml} dangerouslySetInnerHTML={{ __html: openReleaseNote?.bodyHtml ?? '' }} />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};
